import React, { useEffect, useState } from 'react';
import { Employee } from './employeeType';
import EmployeeApi from './employeeApi';
import { useNavigate } from 'react-router-dom';
import HorizontalMenu from '../components/common/horizontalMenu';
import { X, Trash, Mail, MessageSquare, ChevronUp, ChevronDown, FilePlus, Edit, TimerReset } from 'lucide-react';
import Breadcrumbs from '../components/common/breadcrumbs';
import { formatDate, formatTimestamp } from '../lib/global_function';
import { Button } from '../components/ui/button';
import { Alert } from '../components/ui/alert';
import { Badge } from '../components/ui/badge';

const EmployeePage: React.FC = () => {
  const breadcrumbs = [
    { label: 'Company', path: '/main/company' },
    { label: 'Employee', path: '/main/company/employee' },
  ];

  const [errorMessage, setErrorMessage] = useState<string>('');
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [selectedEmployee, setSelectedEmployee] = useState<Employee | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        const fetchedEmployees = await EmployeeApi.get();
        setEmployees(fetchedEmployees);
      } catch (error) {
        if (error instanceof Error) {
          setErrorMessage(error.message);
        } else {
          setErrorMessage('An unknown error occurred.');
        }
      }
    };

    fetchEmployees();
  }, []);

  const handleEditEmployee = (employee: Employee) => {
    navigate('/main/company/employee/update', { state: { employee } });
  };

  const handleEmployeeTimecard = () => {
    if (selectedEmployee) {
      navigate('/main/company/employee/send_timecard', { state: { selectedEmployee } });
    }
  };

  const handleDeleteEmployee = () => {
    if (selectedEmployee) {
      navigate('/main/company/employee/delete', { state: { selectedEmployee } });
    }
  };

    const handleViewTimecard = () => {
    if (selectedEmployee) {
      navigate('/main/company/employee/timecard_report', { state: { selectedEmployee } });
    }
  };

  const handleSortBy = (key: 'last_name' | 'created_at', order: 'asc' | 'desc') => {
    const sortedEmployees = [...employees].sort((a, b) => {
      const compareA = key === 'created_at' ? new Date(a[key]).getTime() : a[key].toLowerCase();
      const compareB = key === 'created_at' ? new Date(b[key]).getTime() : b[key].toLowerCase();
      if (order === 'asc') return compareA > compareB ? 1 : -1;
      return compareA < compareB ? 1 : -1;
    });
    setEmployees(sortedEmployees);
  };

  const handleCreateEmployee = () => {
    navigate('/main/company/employee/create');
  };

  return (
    <>
      <HorizontalMenu />
      <div className="container-safe py-6">
        <Breadcrumbs items={breadcrumbs} />

        <div className="mt-6">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-2">
            <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">Employee List</h1>

            <Button size="medium" variant="default" onClick={handleCreateEmployee}>
              <FilePlus size={18} />
              Create Employee
            </Button>
          </div>
          <p className="text-sm text-paragraph">
            Select the radio button in front of Name to edit, send timecard link, view timecard report, or delete employee. 
          </p>
        </div>

        {errorMessage && (
          <Alert variant="error">
            {errorMessage}
          </Alert>
        )}

        {/* Action Bar - Enhanced with Badge */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg flex flex-wrap items-center gap-3 text-sm">
          <div className="flex items-center gap-2">
            {selectedEmployee ? (
              <>
                <button
                  onClick={() => setSelectedEmployee(null)}
                  className="text-gray-600 hover:text-gray-900"
                  title="Clear Selection"
                  aria-label="Clear selection"
                >
                  <X className="size-5" />
                </button>
                <Badge variant="default">1 employee selected</Badge>
              </>
            ) : (
              <span className="text-gray-500">No employee selected</span>
            )}
          </div>

          <span className="text-gray-300">|</span>
 
          <button
            onClick={() => selectedEmployee && handleEditEmployee(selectedEmployee)}
            disabled={!selectedEmployee}
            className={`flex items-center gap-1.5 ${selectedEmployee
              ? 'text-primary hover:text-primary-hover cursor-pointer'
              : 'text-gray-400 cursor-not-allowed'
              }`}
            title={selectedEmployee ? "Edit Employee" : "Select an employee to edit"}
          >
            <Edit className="size-4" />
            Edit
          </button>

{/** 
          <span className="text-gray-300">|</span>
*/}
          <button
            onClick={handleEmployeeTimecard}
            disabled={!selectedEmployee}
            className={`flex items-center gap-1.5 ${selectedEmployee
              ? 'text-primary hover:text-primary-hover cursor-pointer'
              : 'text-gray-400 cursor-not-allowed'
              }`}
            title={selectedEmployee ? "Send Timecard Link" : "Select an employee to send timecard link"}
          >
            <Mail className="size-4" />
            <MessageSquare className="size-4" />
            Send Timecard Link
          </button>

          <button
            onClick={handleViewTimecard}
            disabled={!selectedEmployee}
            className={`flex items-center gap-1.5 ${selectedEmployee
              ? 'text-primary hover:text-primary-hover cursor-pointer'
              : 'text-gray-400 cursor-not-allowed'
              }`}
            title={selectedEmployee ? "Timecard Report" : "Select an employee to view timecard"}
          >
            <TimerReset className="size-4" />
            Timecard Report
          </button>

{/** 
          <span className="text-gray-300">|</span>
*/}
          <button
            onClick={handleDeleteEmployee}
            disabled={!selectedEmployee}
            className={`flex items-center gap-1.5 ${selectedEmployee
              ? 'text-primary hover:text-primary-hover cursor-pointer'
              : 'text-gray-400 cursor-not-allowed'
              }`}
            title={selectedEmployee ? "Delete Employee" : "Select an employee to delete"}
          >
            <Trash className="size-4" />
            Delete Employee
          </button>
        </div>

        {employees.length > 0 ? (
          <div className="overflow-x-auto border border-gray-200 rounded-lg mt-2.5">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="w-12 px-4 py-3"></th>
                  <th className="px-4 py-3 w-[15%]">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-foreground">Name</span>
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => handleSortBy('last_name', 'asc')}
                          className="text-gray-500 hover:text-gray-700"
                          title="Sort Ascending"
                          aria-label="Sort by name ascending"
                        >
                          <ChevronUp className="size-3" />
                        </button>
                        <button
                          onClick={() => handleSortBy('last_name', 'desc')}
                          className="text-gray-500 hover:text-gray-700"
                          title="Sort Descending"
                          aria-label="Sort by name descending"
                        >
                          <ChevronDown className="size-3" />
                        </button>
                      </div>
                    </div>
                  </th>
                  <th className="px-4 py-3 w-[15%] text-center font-semibold text-foreground">
                    Phone
                  </th>
                  <th className="px-4 py-3 w-[15%] text-center font-semibold text-foreground">
                    Email
                  </th>
                  <th className="px-4 py-3 w-[10%] text-center font-semibold text-foreground">
                    Type
                  </th>
                  <th className="px-4 py-3 w-[10%] text-center font-semibold text-foreground">
                    Title
                  </th>
                  <th className="px-4 py-3 w-[15%] text-center font-semibold text-foreground">
                    Employee Code
                  </th>
                  <th className="px-4 py-3 w-[20%]">
                    <div className="flex flex-col items-center">
                      <span className="font-semibold text-foreground">Created On</span>
                      <div className="flex flex-col items-center">
                        <button
                          onClick={() => handleSortBy('created_at', 'asc')}
                          className="text-gray-500 hover:text-gray-700"
                          title="Sort Ascending"
                          aria-label="Sort by created date ascending"
                        >
                          <ChevronUp className="size-3" />
                        </button>
                        <button
                          onClick={() => handleSortBy('created_at', 'desc')}
                          className="text-gray-500 hover:text-gray-700"
                          title="Sort Descending"
                          aria-label="Sort by created date descending"
                        >
                          <ChevronDown className="size-3" />
                        </button>
                      </div>
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employees.map((employee) => (
                  <tr key={employee.employee_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-center">
                      <input
                        type="radio"
                        name="employeeSelect"
                        onChange={() => setSelectedEmployee(employee)}
                        checked={selectedEmployee?.employee_id === employee.employee_id}
                        className="mt-1"
                        style={{ accentColor: 'rgb(248, 98, 3)' }}
                      />
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleEditEmployee(employee)}
                        className="text-foreground hover:text-primary underline font-medium"
                      >
                        {`${employee.first_name} ${employee.last_name}`}
                      </button>
                    </td>
                    <td className="px-4 py-3 text-center text-paragraph">
                      {employee.phone || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center text-paragraph">
                      {employee.email || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center text-paragraph">
                      {employee.employee_type || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center text-paragraph">
                      {employee.employee_title || 'N/A'}
                    </td>
                    <td className="px-4 py-3 text-center text-paragraph">
                      {employee.employee_code || 'N/A'}
                      <br />
                      <span className="text-sm text-paragraph">
                        (started date: {employee.start_date ? formatDate(employee.start_date) : 'N/A'})
                      </span>
                    </td>
                    <td className="px-4 py-3 text-center text-paragraph">
                      {formatTimestamp(employee.created_at) || 'N/A'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div>
            <p className="text-base text-foreground mb-4">No employees found, please create one.</p>
            <Button onClick={handleCreateEmployee} size="medium" variant="default">
              <FilePlus className="size-5" />
              Create Employee
            </Button>
          </div>
        )}

        <div className="mb-[150px]"></div>
      </div>
    </>
  );
};

export default EmployeePage;